import { z } from "zod";
import { ulidSchema } from "./schemas.js";
import { sharedTestGiftChallengeMetadataSchema } from "./shared-test-gift.js";
export const channelCommerceSchemaVersionSchema = z.literal("2026-09-16.1");
export const commercialChannelSchema = z.enum(["QVC", "HSN", "DIRECT", "INTERNAL_FIXTURE"]);
export const channelOfferStateSchema = z.enum(["DRAFT", "APPROVED", "ACTIVE", "PAUSED", "RETIRED"]);
export const voucherBatchStateSchema = z.enum([
    "RESERVED",
    "GENERATING",
    "READY_TO_EXPORT",
    "EXPORTED",
    "ABORTED",
    "RETIRED",
]);
export const channelVoucherStateSchema = z.enum([
    "CREATED",
    "INVENTORY",
    "ALLOCATED",
    "SHIPPED",
    "REDEEMED",
    "SUSPENDED",
    "LOST",
    "CANCELLED",
    "REVOKED",
    "EXPIRED",
]);
export const channelOrderStateSchema = z.enum([
    "RECEIVED",
    "ACCEPTED",
    "ALLOCATED",
    "CANCELLED",
    "SHIP_CONFIRMED",
    "RETURNED",
    "CLOSED",
]);
export const voucherAllocationStateSchema = z.enum([
    "ALLOCATED",
    "RELEASED",
    "SHIPPED",
    "REPLACED",
    "CANCELLED",
]);
export const voucherPackageStateSchema = z.enum([
    "PENDING",
    "PACKED",
    "SHIPPED",
    "DELIVERED",
    "LOST",
    "RETURNED",
    "CANCELLED",
]);
export const redemptionChallengeStateSchema = z.enum(["PENDING", "CONSUMED", "EXPIRED"]);
export const commercialEntitlementStateSchema = z.enum([
    "UNCLAIMED",
    "CLAIMED",
    "SUSPENDED",
    "REVOKED",
    "REFUNDED",
]);
export const commercialPurchaseIntentSchema = z.enum(["SELF", "GIFT", "UNSPECIFIED"]);
export const voucherBookSelectionPolicySchema = z.enum(["FIXED_EDITION", "CUSTOMER_CHOICE"]);
const eligibleEditionIdsSchema = z
    .array(z.string().min(1).max(100))
    .min(2)
    .max(50)
    .refine((values) => new Set(values).size === values.length, "Eligible editions must be unique.");
const auditFields = {
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
};
export const channelOfferSchema = z
    .object({
    schemaVersion: channelCommerceSchemaVersionSchema,
    id: ulidSchema,
    channel: commercialChannelSchema,
    campaignId: z.string().min(1).max(100),
    channelSku: z.string().min(1).max(100),
    editionId: z.string().min(1).max(100),
    redemptionPolicy: voucherBookSelectionPolicySchema.optional(),
    eligibleEditionIds: eligibleEditionIdsSchema.optional(),
    displayName: z.string().min(1).max(160),
    unitPriceCents: z.number().int().positive(),
    currency: z.literal("USD"),
    state: channelOfferStateSchema,
    test: z.boolean(),
    ...auditFields,
})
    .strict()
    .superRefine((offer, context) => {
    const choice = offer.redemptionPolicy === "CUSTOMER_CHOICE";
    if (choice && !offer.eligibleEditionIds)
        context.addIssue({
            code: "custom",
            path: ["eligibleEditionIds"],
            message: "A customer-choice voucher offer requires eligible editions.",
        });
    if (!choice && offer.eligibleEditionIds)
        context.addIssue({
            code: "custom",
            path: ["eligibleEditionIds"],
            message: "Only a customer-choice voucher offer may list eligible editions.",
        });
    if (choice && offer.eligibleEditionIds && !offer.eligibleEditionIds.includes(offer.editionId))
        context.addIssue({
            code: "custom",
            path: ["editionId"],
            message: "The offer's reference edition must be eligible for redemption.",
        });
});
export const voucherExportReceiptSchema = z
    .object({
    digestSha256: z.string().regex(/^[a-f0-9]{64}$/u),
    rowCount: z.number().int().positive().max(100_000),
    exportedAt: z.string().datetime(),
    exportedBy: z.string().min(1).max(200),
    recovered: z.boolean(),
})
    .strict();
export const voucherBatchSchema = z
    .object({
    schemaVersion: channelCommerceSchemaVersionSchema,
    id: ulidSchema,
    offerId: ulidSchema,
    quantity: z.number().int().positive().max(100_000),
    codePrefix: z.string().regex(/^[A-HJ-NP-Z2-9]{2,10}$/u),
    state: voucherBatchStateSchema,
    exportReceipt: voucherExportReceiptSchema.optional(),
    test: z.boolean(),
    ...auditFields,
})
    .strict();
export const channelVoucherSchema = z
    .object({
    schemaVersion: channelCommerceSchemaVersionSchema,
    id: ulidSchema,
    batchId: ulidSchema,
    offerId: ulidSchema,
    codeHash: z.string().regex(/^[a-f0-9]{64}$/u),
    state: channelVoucherStateSchema,
    orderId: ulidSchema.optional(),
    channelOrderId: z.string().min(1).max(200).optional(),
    packageId: ulidSchema.optional(),
    entitlementId: ulidSchema.optional(),
    entitlementRequired: z.literal(true).optional(),
    shipConfirmationDigestSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/u)
        .optional(),
    replacementForVoucherId: ulidSchema.optional(),
    replacedByVoucherId: ulidSchema.optional(),
    redeemedAt: z.string().datetime().optional(),
    test: z.boolean(),
    ...auditFields,
})
    .strict()
    .superRefine((voucher, context) => {
    if (voucher.entitlementRequired && !voucher.entitlementId)
        context.addIssue({
            code: "custom",
            path: ["entitlementId"],
            message: "A modern channel voucher requires its commercial entitlement.",
        });
});
export const channelOrderSchema = z
    .object({
    schemaVersion: channelCommerceSchemaVersionSchema,
    id: ulidSchema,
    offerId: ulidSchema,
    channel: commercialChannelSchema,
    channelOrderId: z.string().min(1).max(200),
    merchantOrderId: z.string().min(1).max(200),
    fixtureDigestSha256: z.string().regex(/^[a-f0-9]{64}$/u),
    shipConfirmationDigestSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/u)
        .optional(),
    state: channelOrderStateSchema,
    quantity: z.number().int().positive().max(100),
    test: z.boolean(),
    ...auditFields,
})
    .strict();
export const voucherAllocationSchema = z
    .object({
    schemaVersion: channelCommerceSchemaVersionSchema,
    id: ulidSchema,
    orderId: ulidSchema,
    voucherId: ulidSchema,
    packageId: ulidSchema,
    entitlementId: ulidSchema.optional(),
    entitlementRequired: z.literal(true).optional(),
    shipConfirmationDigestSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/u)
        .optional(),
    packageJourneyDigestSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/u)
        .optional(),
    packageSubmittedAt: z.string().datetime().optional(),
    deliveredAt: z.string().datetime().optional(),
    channelOrderId: z.string().min(1).max(200),
    merchantSku: z.string().min(1).max(100),
    unitPosition: z.number().int().positive().max(100),
    state: voucherAllocationStateSchema,
    idempotencyKey: z.string().min(8).max(200),
    replacementForAllocationId: ulidSchema.optional(),
    replacedByAllocationId: ulidSchema.optional(),
    test: z.boolean(),
    ...auditFields,
})
    .strict()
    .superRefine((allocation, context) => {
    if (allocation.entitlementRequired && !allocation.entitlementId)
        context.addIssue({
            code: "custom",
            path: ["entitlementId"],
            message: "A modern voucher allocation requires its commercial entitlement.",
        });
    const packageJourneyFields = [
        allocation.packageJourneyDigestSha256,
        allocation.packageSubmittedAt,
        allocation.deliveredAt,
    ];
    const packageJourneyFieldCount = packageJourneyFields.filter((value) => value !== undefined).length;
    if (packageJourneyFieldCount !== 0 && packageJourneyFieldCount !== packageJourneyFields.length)
        context.addIssue({
            code: "custom",
            path: ["packageJourneyDigestSha256"],
            message: "Package journey evidence requires its digest, submission time, and delivery time.",
        });
    if (packageJourneyFieldCount === packageJourneyFields.length) {
        if (allocation.state !== "SHIPPED" && allocation.state !== "REPLACED")
            context.addIssue({
                code: "custom",
                path: ["state"],
                message: "Package journey evidence requires a shipped or replaced allocation.",
            });
        if (!allocation.shipConfirmationDigestSha256)
            context.addIssue({
                code: "custom",
                path: ["shipConfirmationDigestSha256"],
                message: "Package journey evidence requires shipment evidence.",
            });
        if (Date.parse(allocation.deliveredAt) < Date.parse(allocation.packageSubmittedAt))
            context.addIssue({
                code: "custom",
                path: ["deliveredAt"],
                message: "Package delivery cannot predate package submission.",
            });
    }
});
export const commercialEntitlementSchema = z
    .object({
    schemaVersion: channelCommerceSchemaVersionSchema,
    id: ulidSchema,
    purchaseChannel: z.enum(["QVC", "DIRECT"]),
    orderId: ulidSchema,
    currentVoucherId: ulidSchema,
    editionId: z.string().min(1).max(100),
    redemptionPolicy: voucherBookSelectionPolicySchema.optional(),
    eligibleEditionIds: eligibleEditionIdsSchema.optional(),
    purchaseIntent: commercialPurchaseIntentSchema,
    includedPrintCopies: z.literal(1),
    state: commercialEntitlementStateSchema,
    offerId: ulidSchema.optional(),
    checkoutId: ulidSchema.optional(),
    channelOrderId: z.string().min(1).max(200).optional(),
    merchantSku: z.string().min(1).max(100).optional(),
    unitPosition: z.number().int().positive().max(100).optional(),
    claimedBy: ulidSchema.optional(),
    bookId: ulidSchema.optional(),
    claimedAt: z.string().datetime().optional(),
    stateBeforeSuspension: z.enum(["UNCLAIMED", "CLAIMED"]).optional(),
    reversedAt: z.string().datetime().optional(),
    reversalReason: z
        .string()
        .regex(/^[A-Z][A-Z0-9_]{1,63}$/u)
        .optional(),
    test: z.boolean(),
    ...auditFields,
})
    .strict()
    .superRefine((entitlement, context) => {
    const choice = entitlement.redemptionPolicy === "CUSTOMER_CHOICE";
    if (choice && !entitlement.eligibleEditionIds)
        context.addIssue({
            code: "custom",
            path: ["eligibleEditionIds"],
            message: "A customer-choice entitlement requires eligible editions.",
        });
    if (!choice && entitlement.eligibleEditionIds)
        context.addIssue({
            code: "custom",
            path: ["eligibleEditionIds"],
            message: "Only a customer-choice entitlement may list eligible editions.",
        });
    if (choice &&
        entitlement.eligibleEditionIds &&
        !entitlement.eligibleEditionIds.includes(entitlement.editionId))
        context.addIssue({
            code: "custom",
            path: ["editionId"],
            message: "The selected entitlement edition must remain in the purchased choice set.",
        });
    if (entitlement.purchaseChannel === "DIRECT") {
        if (!entitlement.checkoutId)
            context.addIssue({
                code: "custom",
                path: ["checkoutId"],
                message: "A direct entitlement requires its checkout identity.",
            });
        if (entitlement.purchaseIntent === "UNSPECIFIED")
            context.addIssue({
                code: "custom",
                path: ["purchaseIntent"],
                message: "A direct entitlement requires self or gift intent.",
            });
        if (entitlement.offerId ||
            entitlement.channelOrderId ||
            entitlement.merchantSku ||
            entitlement.unitPosition)
            context.addIssue({
                code: "custom",
                path: ["purchaseChannel"],
                message: "A direct entitlement cannot carry channel-order provenance.",
            });
    }
    else {
        if (!entitlement.offerId ||
            !entitlement.channelOrderId ||
            !entitlement.merchantSku ||
            !entitlement.unitPosition)
            context.addIssue({
                code: "custom",
                path: ["purchaseChannel"],
                message: "A QVC entitlement requires offer and exact order-unit provenance.",
            });
        if (entitlement.checkoutId || entitlement.purchaseIntent !== "UNSPECIFIED")
            context.addIssue({
                code: "custom",
                path: ["purchaseChannel"],
                message: "A QVC entitlement must preserve unspecified recipient intent.",
            });
    }
    const claimFields = [entitlement.claimedBy, entitlement.bookId, entitlement.claimedAt];
    const claimFieldCount = claimFields.filter((value) => value !== undefined).length;
    if (claimFieldCount !== 0 && claimFieldCount !== claimFields.length)
        context.addIssue({
            code: "custom",
            path: ["claimedBy"],
            message: "Claim ownership, book identity, and time must be recorded together.",
        });
    if (entitlement.state === "CLAIMED" && claimFieldCount !== claimFields.length)
        context.addIssue({
            code: "custom",
            path: ["state"],
            message: "A claimed entitlement requires its owner, book, and claim time.",
        });
    if (entitlement.state === "UNCLAIMED" && claimFieldCount !== 0)
        context.addIssue({
            code: "custom",
            path: ["state"],
            message: "An unclaimed entitlement cannot retain a book claim.",
        });
    if (entitlement.state === "SUSPENDED") {
        if (!entitlement.stateBeforeSuspension)
            context.addIssue({
                code: "custom",
                path: ["stateBeforeSuspension"],
                message: "A suspended entitlement requires its prior state.",
            });
        if (entitlement.stateBeforeSuspension === "CLAIMED" && claimFieldCount !== claimFields.length)
            context.addIssue({
                code: "custom",
                path: ["claimedBy"],
                message: "A suspended claimed entitlement must retain its book claim.",
            });
        if (entitlement.stateBeforeSuspension === "UNCLAIMED" && claimFieldCount !== 0)
            context.addIssue({
                code: "custom",
                path: ["claimedBy"],
                message: "A suspended unclaimed entitlement cannot have a book claim.",
            });
    }
    else if (entitlement.stateBeforeSuspension)
        context.addIssue({
            code: "custom",
            path: ["stateBeforeSuspension"],
            message: "Only a suspended entitlement records its prior state.",
        });
    const reversed = entitlement.state === "REVOKED" || entitlement.state === "REFUNDED";
    if ((reversed && (!entitlement.reversedAt || !entitlement.reversalReason)) ||
        (!reversed &&
            (entitlement.reversedAt !== undefined || entitlement.reversalReason !== undefined)))
        context.addIssue({
            code: "custom",
            path: ["reversedAt"],
            message: "Only a terminal entitlement may carry both a reversal time and reason.",
        });
});
export const voucherPackageSchema = z
    .object({
    schemaVersion: channelCommerceSchemaVersionSchema,
    id: ulidSchema,
    orderId: ulidSchema,
    packageRevision: z.string().min(1).max(100),
    state: voucherPackageStateSchema,
    carrier: z.string().min(1).max(100).optional(),
    trackingReference: z.string().min(1).max(200).optional(),
    shipConfirmationDigestSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/u)
        .optional(),
    packageJourneyDigestSha256: z
        .string()
        .regex(/^[a-f0-9]{64}$/u)
        .optional(),
    packageSubmittedAt: z.string().datetime().optional(),
    deliveredAt: z.string().datetime().optional(),
    replacementForPackageId: ulidSchema.optional(),
    test: z.boolean(),
    ...auditFields,
})
    .strict()
    .superRefine((voucherPackage, context) => {
    const packageJourneyFields = [
        voucherPackage.packageJourneyDigestSha256,
        voucherPackage.packageSubmittedAt,
        voucherPackage.deliveredAt,
    ];
    const packageJourneyFieldCount = packageJourneyFields.filter((value) => value !== undefined).length;
    if (packageJourneyFieldCount !== 0 && packageJourneyFieldCount !== packageJourneyFields.length)
        context.addIssue({
            code: "custom",
            path: ["packageJourneyDigestSha256"],
            message: "A delivered package requires its journey digest and timestamps.",
        });
    if (voucherPackage.state === "DELIVERED" &&
        packageJourneyFieldCount !== packageJourneyFields.length)
        context.addIssue({
            code: "custom",
            path: ["state"],
            message: "A delivered package requires explicit package-journey evidence.",
        });
    if (packageJourneyFieldCount === packageJourneyFields.length) {
        if (voucherPackage.state !== "DELIVERED" &&
            voucherPackage.state !== "LOST" &&
            voucherPackage.state !== "RETURNED")
            context.addIssue({
                code: "custom",
                path: ["state"],
                message: "Package journey evidence requires a delivered package state.",
            });
        if (!voucherPackage.shipConfirmationDigestSha256)
            context.addIssue({
                code: "custom",
                path: ["shipConfirmationDigestSha256"],
                message: "Package journey evidence requires shipment evidence.",
            });
        if (Date.parse(voucherPackage.deliveredAt) < Date.parse(voucherPackage.packageSubmittedAt))
            context.addIssue({
                code: "custom",
                path: ["deliveredAt"],
                message: "Package delivery cannot predate package submission.",
            });
    }
});
export const commerceHubShapedOrderFixtureSchema = z
    .object({
    fixtureKind: z.literal("COMMERCEHUB_SHAPED_NON_PRODUCTION"),
    schemaVersion: channelCommerceSchemaVersionSchema,
    merchantOrderId: z.string().min(1).max(200),
    channelOrderId: z.string().min(1).max(200),
    orderPlacedAt: z.string().datetime(),
    requestedShipBy: z.string().datetime(),
    shipTo: z
        .object({
        recipientName: z.string().min(1).max(200),
        addressLine1: z.string().min(1).max(200),
        addressLine2: z.string().max(200).optional(),
        city: z.string().min(1).max(100),
        region: z.string().min(1).max(100),
        postalCode: z.string().min(1).max(24),
        countryCode: z.string().length(2),
    })
        .strict(),
    lines: z
        .array(z
        .object({
        merchantSku: z.string().min(1).max(100),
        quantity: z.number().int().positive().max(100),
    })
        .strict())
        .min(1)
        .max(100),
})
    .strict()
    .superRefine((fixture, context) => {
    if (new Set(fixture.lines.map((line) => line.merchantSku)).size !== fixture.lines.length)
        context.addIssue({
            code: "custom",
            path: ["lines"],
            message: "A fixture order cannot repeat a merchant SKU line.",
        });
    if (fixture.lines.reduce((total, line) => total + line.quantity, 0) > 100)
        context.addIssue({
            code: "custom",
            path: ["lines"],
            message: "A fixture order cannot exceed 100 units.",
        });
});
export const commerceHubShapedShipConfirmationFixtureSchema = z
    .object({
    fixtureKind: z.literal("COMMERCEHUB_SHAPED_NON_PRODUCTION"),
    schemaVersion: channelCommerceSchemaVersionSchema,
    merchantOrderId: z.string().min(1).max(200),
    shipmentId: z.string().min(1).max(200),
    shippedAt: z.string().datetime(),
    carrier: z.string().min(1).max(100),
    trackingReference: z.string().min(1).max(200),
    lines: z
        .array(z
        .object({
        merchantSku: z.string().min(1).max(100),
        quantity: z.number().int().positive().max(100),
    })
        .strict())
        .min(1)
        .max(100),
})
    .strict()
    .superRefine((fixture, context) => {
    if (new Set(fixture.lines.map((line) => line.merchantSku)).size !== fixture.lines.length)
        context.addIssue({
            code: "custom",
            path: ["lines"],
            message: "A fixture shipment cannot repeat a merchant SKU line.",
        });
    if (fixture.lines.reduce((total, line) => total + line.quantity, 0) > 100)
        context.addIssue({
            code: "custom",
            path: ["lines"],
            message: "A fixture shipment cannot exceed 100 units.",
        });
});
export const voucherPackageJourneyFixtureSchema = z
    .object({
    fixtureKind: z.literal("VOUCHER_PACKAGE_JOURNEY_SHAPED_NON_PRODUCTION"),
    schemaVersion: channelCommerceSchemaVersionSchema,
    merchantOrderId: z.string().min(1).max(200),
    shipmentId: z.string().min(1).max(200),
    packageSubmissionId: z.string().min(1).max(200),
    deliveryEventId: z.string().min(1).max(200),
    packageRevision: z.string().min(1).max(100),
    submittedAt: z.string().datetime(),
    deliveredAt: z.string().datetime(),
    carrier: z.string().min(1).max(100),
    trackingReference: z.string().min(1).max(200),
    lines: z
        .array(z
        .object({
        merchantSku: z.string().min(1).max(100),
        quantity: z.number().int().positive().max(100),
    })
        .strict())
        .min(1)
        .max(100),
})
    .strict()
    .superRefine((fixture, context) => {
    if (new Set(fixture.lines.map((line) => line.merchantSku)).size !== fixture.lines.length)
        context.addIssue({
            code: "custom",
            path: ["lines"],
            message: "A package-journey fixture cannot repeat a merchant SKU line.",
        });
    if (fixture.lines.reduce((total, line) => total + line.quantity, 0) > 100)
        context.addIssue({
            code: "custom",
            path: ["lines"],
            message: "A package-journey fixture cannot exceed 100 units.",
        });
    if (Date.parse(fixture.deliveredAt) < Date.parse(fixture.submittedAt))
        context.addIssue({
            code: "custom",
            path: ["deliveredAt"],
            message: "Package delivery cannot predate package submission.",
        });
});
export const createVoucherRedemptionChallengeCommandSchema = z
    .object({ code: z.string().min(8).max(64) })
    .strict();
const voucherRedemptionChallengeBaseSchema = z.object({
    challengeToken: z.string().regex(/^[A-Za-z0-9_-]{43}$/u),
    expiresAt: z.string().datetime(),
    offerName: z.string().min(1).max(160),
    test: z.boolean(),
});
const voucherTemplateOptionSchema = z
    .object({
    templateId: z.string().min(1).max(100),
    name: z.string().min(2).max(160),
    workflowKind: z.enum(["INTERVIEW", "PICTURE_BOOK"]),
    availability: z.enum(["WORKING", "SANDBOX"]),
    readiness: z.enum(["AVAILABLE_WORKFLOW", "SANDBOX_WORKFLOW"]),
})
    .strict();
/**
 * What a book voucher includes beyond the book itself: one printed copy when its purchase
 * entitlement carries one, otherwise none. A shared practice pass never includes print.
 */
const voucherInclusionsShape = {
    includedPrintCopies: z.union([z.literal(0), z.literal(1)]),
};
export const createVoucherRedemptionChallengeResponseSchema = z
    .object({
    data: z.union([
        voucherRedemptionChallengeBaseSchema
            .extend({
            kind: z.literal("VOUCHER"),
            reusable: z.literal(false),
            selectionRequired: z.literal(false),
            /** The one book this voucher makes. */
            templateId: z.string().min(1).max(100),
            ...voucherInclusionsShape,
        })
            .strict(),
        voucherRedemptionChallengeBaseSchema
            .extend({
            kind: z.literal("VOUCHER"),
            reusable: z.literal(false),
            selectionRequired: z.literal(true),
            ...voucherInclusionsShape,
            allowedTemplates: z
                .array(voucherTemplateOptionSchema)
                .min(2)
                .max(50)
                .refine((templates) => new Set(templates.map(({ templateId }) => templateId)).size === templates.length, "Allowed voucher editions must be unique."),
        })
            .strict(),
        voucherRedemptionChallengeBaseSchema
            .extend(sharedTestGiftChallengeMetadataSchema.shape)
            .strict(),
    ]),
})
    .strict();
export const consumeVoucherRedemptionChallengeCommandSchema = z
    .object({ challengeToken: z.string().regex(/^[A-Za-z0-9_-]{43}$/u) })
    .strict();
export const createBuyerRehearsalSessionCommandSchema = z
    .object({ inviteCode: z.string().min(8).max(64) })
    .strict();
export const createBuyerRehearsalSessionResponseSchema = z
    .object({
    data: z
        .object({
        sessionToken: z.string().regex(/^[A-Za-z0-9_-]{43}$/u),
        expiresAt: z.string().datetime(),
        editionId: z.literal("lantern-fox-v1"),
        rehearsal: z.literal(true),
    })
        .strict(),
})
    .strict();
//# sourceMappingURL=channel-commerce.js.map